import { useMutation } from '@tanstack/react-query'
import { REGEXP_ONLY_DIGITS } from 'input-otp'
import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'

import { Button } from '@/components/elements/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/elements/dialog'
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/elements/form'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/elements/input-otp'
import { resendUserEmailVerification, verifyUserEmail } from '@/services/user.service'
import { toast } from '@/utils/helpers'

const CODE_LENGTH = 6

/**
 * Confirm an added email address with its emailed code (RSCX-351). Until
 * verified, the address can't sign in or receive notifications — mirrors the
 * phone number verification dialog.
 */
const VerifyEmailDialog = ({ userId, email, onClose, onVerified }) => {
  const [cooldown, setCooldown] = useState(0)
  const intervalRef = useRef(null)

  const form = useForm({ defaultValues: { code: '' } })

  const { mutate: verify, isPending: verifying } = useMutation({
    mutationFn: (code) => verifyUserEmail({ userId, emailId: email.id, code }),
    onError: (err) => toast(err.message, 'error'),
    onSuccess: () => {
      toast(`${email.email} verified`, 'success')
      onVerified()
    },
  })

  const { mutate: resend, isPending: resending } = useMutation({
    mutationFn: () => resendUserEmailVerification({ userId, emailId: email.id }),
    onError: (err) => toast(err.message, 'error'),
    onSuccess: () => {
      toast('Verification code sent', 'success')
      setCooldown(10)
      intervalRef.current = setInterval(() => {
        setCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
            return 0
          }
          return prev - 1
        })
      }, 1000)
    },
  })

  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => clearInterval(intervalRef.current)
  }, [])

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Verify Your Email Address</DialogTitle>
          <DialogDescription>
            We&apos;ve sent a verification code to {email.email}. The address can&apos;t be used to
            sign in or receive notifications until it&apos;s verified.
            <br />
            Didn&apos;t receive it?{' '}
            {cooldown > 0 ? (
              <span className="text-primary text-sm font-medium">
                Try again in {cooldown} seconds.
              </span>
            ) : (
              <Button
                disabled={resending}
                type="button"
                variant="link"
                size="plain"
                className="text-primary text-sm font-medium"
                onClick={() => resend()}
              >
                Resend Code
              </Button>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-6 flex w-full flex-col">
          <Form {...form}>
            <form onSubmit={form.handleSubmit((data) => verify(data.code))}>
              <FormField
                name="code"
                control={form.control}
                rules={{ required: true }}
                disabled={verifying}
                render={({ field }) => (
                  <FormItem className="justify-center">
                    <FormControl>
                      <InputOTP maxLength={CODE_LENGTH} pattern={REGEXP_ONLY_DIGITS} {...field}>
                        <InputOTPGroup>
                          {[...Array(CODE_LENGTH)].map((_, index) => (
                            <InputOTPSlot
                              // eslint-disable-next-line react/no-array-index-key
                              key={`slot-${index}`}
                              index={index}
                              autoFocus={index === 0}
                            />
                          ))}
                        </InputOTPGroup>
                      </InputOTP>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="mt-8 flex justify-end gap-2">
                <Button type="button" variant="secondary" onClick={onClose}>
                  Verify Later
                </Button>
                <Button type="submit" loading={verifying}>
                  Verify Email
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  )
}

VerifyEmailDialog.propTypes = {
  userId: PropTypes.string.isRequired,
  email: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onVerified: PropTypes.func.isRequired,
}

export default VerifyEmailDialog
